<template>
  <div class="summaries-per-task">
    <b-loading :active="isLoading" :is-full-page="false"></b-loading>

    <!-- All tasks summary -->
    <div v-if="!isLoading" class="pt-summary">
      <div class="pt-summary__head">
        <span class="pt-summary__title">{{ $t('grouped-logs.project-tasks-title') }}</span>
        <div class="pt-summary__headline">
          <span class="pt-summary__amount">{{
              groupedLogsSum.expected_total_fee !== undefined ? toFixedIfNecessary(groupedLogsSum.expected_total_fee) : 0
            }} {{ defaultValues.priceUnit }}</span>
          <span class="pt-summary__caption">{{ $t('grouped-logs.expected-total-fee-title') }}</span>
        </div>
      </div>
      <div class="pt-body">
        <div class="pt-metrics">
          <div class="pt-metric">
            <span class="pt-metric__label">{{ $t('grouped-logs.expected-executed-fee-title') }} ({{ groupedLogsSum.progress_percentage_work }}%)</span>
            <span class="pt-metric__value">{{
                groupedLogsSum.expected_executed_fee !== undefined ? toFixedIfNecessary(groupedLogsSum.expected_executed_fee) : 0
              }} {{ defaultValues.priceUnit }}</span>
          </div>
          <div class="pt-metric">
            <span class="pt-metric__label">{{ $t('grouped-logs.executed-fee-title') }} ({{ groupedLogsSum.progress_percentage_budget }}%)</span>
            <span class="pt-metric__value">{{
                groupedLogsSum.executed_fee !== undefined ? toFixedIfNecessary(groupedLogsSum.executed_fee) : 0
              }} {{ defaultValues.priceUnit }}</span>
          </div>
          <div class="pt-metric">
            <span class="pt-metric__label">{{ $t('grouped-logs.profit-title') }}</span>
            <span class="pt-metric__value" :style="profitTextStyle(groupedLogsSum.profit)">{{
                toFixedIfNecessary(nullToZero(groupedLogsSum.profit))
              }} {{ defaultValues.priceUnit }}</span>
          </div>
          <div class="pt-metric">
            <span class="pt-metric__label">{{ $t('grouped-logs.expected-end-profit-title') }}</span>
            <span class="pt-metric__value" :style="profitTextStyle(groupedLogsSum.expected_end_profit)">{{
                toFixedIfNecessary(nullToZero(groupedLogsSum.expected_end_profit))
              }} {{ defaultValues.priceUnit }}</span>
          </div>
        </div>
        <div class="pt-bars">
          <div class="pt-bar">
            <div class="pt-bar__top">
              <span class="pt-bar__label">{{ $t('grouped-logs.progress-percentage-quantity-title') }}</span>
              <span class="pt-bar__pct">{{ toFixedIfNecessary(nullToZero(groupedLogsSum.progress_percentage_work), 1) }}%</span>
            </div>
            <div class="pt-bar__track">
              <i class="pt-bar__fill pt-bar__fill--brand"
                 :style="{ width: barWidth(groupedLogsSum.progress_percentage_work) }"></i>
            </div>
          </div>
          <div class="pt-bar">
            <div class="pt-bar__top">
              <span class="pt-bar__label">{{ $t('grouped-logs.progress-percentage-fee-title') }}</span>
              <span class="pt-bar__pct">{{ toFixedIfNecessary(nullToZero(groupedLogsSum.progress_percentage_budget), 1) }}%</span>
            </div>
            <div class="pt-bar__track">
              <i class="pt-bar__fill" :class="nullToZero(groupedLogsSum.profit) >= 0 ? 'pt-bar__fill--good' : 'pt-bar__fill--bad'"
                 :style="{ width: barWidth(groupedLogsSum.progress_percentage_budget) }"></i>
            </div>
          </div>
        </div>
      </div>
    </div>

    <!-- Per task -->
    <div v-for="(row, i) in groupedLogsData" :key="i" class="pt-card">
      <div class="pt-card__head">
        <span class="pt-title">{{ row.title }}</span>
      </div>
      <div class="pt-body">
        <div class="pt-metrics">
          <div class="pt-metric">
            <span class="pt-metric__label">{{ $t('grouped-logs.logged-quantities-title') }}</span>
            <span class="pt-metric__value">{{ toFixedIfNecessary(row.logged_quantities) }} {{ getTranslatedQuantityUnit(row.unit) }}</span>
          </div>
          <div class="pt-metric">
            <span class="pt-metric__label">{{ $t('grouped-logs.quantity-title') }}</span>
            <span class="pt-metric__value">{{ toFixedIfNecessary(row.expected_quantity) }} {{ getTranslatedQuantityUnit(row.unit) }}</span>
          </div>
          <div class="pt-metric">
            <span class="pt-metric__label">{{ $t('grouped-logs.executed-performance') }}</span>
            <span class="pt-metric__value">{{ toFixedIfNecessary(nullToZero(row.executed_performance)) }} {{ getPerformanceUnit(row.unit) }}</span>
          </div>
          <div class="pt-metric">
            <span class="pt-metric__label">{{ $t('grouped-logs.expected-performance') }}</span>
            <span class="pt-metric__value">{{ toFixedIfNecessary(nullToZero(row.expected_performance)) }} {{ getPerformanceUnit(row.unit) }}</span>
          </div>

          <div class="pt-metric">
            <span class="pt-metric__label">{{ $t('grouped-logs.executed-fee-title') }}</span>
            <span class="pt-metric__value">{{ toFixedIfNecessary(row.executed_fee) }} {{ defaultValues.priceUnit }}</span>
          </div>
          <div class="pt-metric">
            <span class="pt-metric__label">{{ $t('grouped-logs.expected-executed-fee-title') }}</span>
            <span class="pt-metric__value">{{ toFixedIfNecessary(nullToZero(row.expected_executed_fee)) }} {{ defaultValues.priceUnit }}</span>
          </div>
          <div class="pt-metric">
            <span class="pt-metric__label">{{ $t('grouped-logs.expected-total-fee-title') }}</span>
            <span class="pt-metric__value">{{ toFixedIfNecessary(row.expected_total_fee) }} {{ defaultValues.priceUnit }}</span>
          </div>

          <div class="pt-metric">
            <span class="pt-metric__label">{{ $t('grouped-logs.logged-internal-duration-title') }}</span>
            <span class="pt-metric__value internal-text">{{ getFormattedTime(row.logged_internal_durations) }} {{ $t("enums.duration-unit-short.hour") }}</span>
            <span class="pt-metric__sub">({{ toFixedIfNecessary(row.executed_internal_fee) }} {{ defaultValues.priceUnit }})</span>
          </div>
          <div class="pt-metric">
            <span class="pt-metric__label">{{ $t('grouped-logs.expected-internal-duration-title') }}</span>
            <span class="pt-metric__value internal-text">{{ getFormattedTime(row.expected_internal_hours) }} {{ $t("enums.duration-unit-short.hour") }}</span>
            <span class="pt-metric__sub">({{ (row.expected_internal_hours * row.internal_personal_cost).toFixed(0) }} {{ defaultValues.priceUnit }})</span>
          </div>
          <div class="pt-metric">
            <span class="pt-metric__label">{{ $t('grouped-logs.logged-external-duration-title') }}</span>
            <span class="pt-metric__value external-text">{{ getFormattedTime(row.logged_external_durations) }} {{ $t("enums.duration-unit-short.hour") }}</span>
            <span class="pt-metric__sub">({{ toFixedIfNecessary(row.executed_external_fee) }} {{ defaultValues.priceUnit }})</span>
          </div>
          <div class="pt-metric">
            <span class="pt-metric__label">{{ $t('grouped-logs.expected-external-duration-title') }}</span>
            <span class="pt-metric__value external-text">{{ getFormattedTime(row.expected_external_hours) }} {{ $t("enums.duration-unit-short.hour") }}</span>
            <span class="pt-metric__sub">({{ (row.expected_external_hours * row.external_personal_cost).toFixed(0) }} {{ defaultValues.priceUnit }})</span>
          </div>

          <div class="pt-metric">
            <span class="pt-metric__label">{{ $t('grouped-logs.profit-title') }}</span>
            <span class="pt-metric__value" :style="profitTextStyle(row.profit)">{{
                (row.profit) !== null ? toFixedIfNecessary(row.profit) : 0
              }} {{ defaultValues.priceUnit }}</span>
          </div>
          <div class="pt-metric">
            <span class="pt-metric__label">{{ $t('grouped-logs.expected-end-profit-title') }}</span>
            <span class="pt-metric__value" :style="profitTextStyle(row.expected_end_profit)">{{
                (row.expected_end_profit) !== null ? toFixedIfNecessary(row.expected_end_profit) : 0
              }} {{ defaultValues.priceUnit }}</span>
          </div>
        </div>

        <div class="pt-bars">
          <div class="pt-bar">
            <div class="pt-bar__top">
              <span class="pt-bar__label">{{ $t('grouped-logs.progress-percentage-quantity-title') }}</span>
              <span class="pt-bar__pct">{{
                  (row.progress_percentage_quantity) !== null ? toFixedIfNecessary(row.progress_percentage_quantity, 1) : 0
                }}%</span>
            </div>
            <div class="pt-bar__track">
              <i class="pt-bar__fill pt-bar__fill--brand"
                 :style="{ width: barWidth(row.progress_percentage_quantity) }"></i>
            </div>
          </div>
          <div class="pt-bar">
            <div class="pt-bar__top">
              <span class="pt-bar__label">{{ $t('grouped-logs.progress-percentage-fee-title') }}</span>
              <span class="pt-bar__pct">{{
                  (row.progress_percentage_fee) != null ? toFixedIfNecessary(row.progress_percentage_fee, 1) : 0
                }}%</span>
            </div>
            <div class="pt-bar__track">
              <i class="pt-bar__fill" :class="nullToZero(row.profit) >= 0 ? 'pt-bar__fill--good' : 'pt-bar__fill--bad'"
                 :style="{ width: barWidth(row.progress_percentage_fee) }"></i>
            </div>
          </div>
        </div>
      </div>
    </div>
  </div>
</template>

<script>
import firebase from "firebase/app";

export default {
  name: "GroupedLogsPerTask",
  components: {},
  props: {
    'project_id': {
      type: String,
    }
  },
  data() {
    return {
      isLoading: false,
      groupedLogsData: [],
      groupedLogsSum: {
        progress_percentage_work: 0,
        progress_percentage_budget: 0
      },
    }
  },
  methods: {
    readProjectSummaryByTask: function () {
      this.isLoading = true
      this.groupedLogsData = []
      this.groupedLogsSum = []
      const functions = firebase.app().functions(this.$brand.functionsRegion);
      const getProjectSummaryFunction = functions.httpsCallable(this.getSubmitEndpoint());
      getProjectSummaryFunction({action: "get_project_summary_by_task", document_id: this.project_id, fill_meta: true})
          .then((result) => {
            this.isLoading = false
            this.groupedLogsData = result.data["grouped_tasks"]
            this.groupedLogsSum = result.data["sum"]
            if (this.groupedLogsSum.expected_total_fee !== 0) {
              this.groupedLogsSum.progress_percentage_work = ((this.groupedLogsSum.expected_executed_fee / this.groupedLogsSum.expected_total_fee) * 100).toFixed(2)
              this.groupedLogsSum.progress_percentage_budget = ((this.groupedLogsSum.executed_fee / this.groupedLogsSum.expected_total_fee) * 100).toFixed(2)
            }
          })
          .catch((error) => {
            this.isLoading = false
            this.showError(error.message)
            console.log("Error get_project_summary_by_task: ", error?.code);
          });
    },
    nullToZero: function(value) {
      if (value == null || value === "" || value === undefined)
        return 0
      else
        return value
    },
    barWidth: function (value) {
      const v = this.nullToZero(value)
      return Math.min(Math.max(parseFloat(v) || 0, 0), 100) + '%'
    }
  },
  mounted() {
    this.readProjectSummaryByTask();
  }
};
</script>

<style scoped lang="scss">
@import "@/assets/scss/tokens";

.summaries-per-task {
  position: relative;
  display: flex;
  flex-direction: column;
  gap: 14px;
  min-height: 200px;
}

.pt-summary,
.pt-card {
  border: 1px solid $omx-ink-100;
  border-radius: $omx-radius-lg;
  padding: 18px;
  background: $omx-surface;
}
.pt-summary { background: $omx-ink-50; }

.pt-summary__head {
  display: flex;
  align-items: baseline;
  justify-content: space-between;
  gap: 16px;
  flex-wrap: wrap;
  margin-bottom: 16px;
}
.pt-summary__title { font-size: 18px; font-weight: 800; letter-spacing: -.01em; color: $omx-ink-900; }
.pt-summary__amount { font-size: 20px; font-weight: 800; font-variant-numeric: tabular-nums; color: $omx-ink-900; }
.pt-summary__caption { color: $omx-ink-500; margin-left: 8px; font-size: 13px; }

.pt-card__head { margin-bottom: 14px; }
.pt-title { font-size: 16px; font-weight: 700; color: $omx-ink-900; }

// Body: metric grid + progress bars side by side, stacking on narrow widths.
.pt-body {
  display: grid;
  grid-template-columns: minmax(0, 1fr) 250px;
  gap: 20px;
}
.pt-metrics {
  display: grid;
  grid-template-columns: repeat(auto-fit, minmax(150px, 1fr));
  gap: 12px 18px;
  align-content: start;
}
.pt-metric { display: flex; flex-direction: column; gap: 2px; }
.pt-metric__label {
  font-size: 11px;
  font-weight: 700;
  letter-spacing: .04em;
  text-transform: uppercase;
  color: $omx-ink-500;
}
.pt-metric__value { font-size: 15px; font-weight: 700; font-variant-numeric: tabular-nums; color: $omx-ink-900; }
.pt-metric__sub { font-size: 11px; font-weight: 600; color: $omx-ink-500; }

.pt-bars {
  display: flex;
  flex-direction: column;
  gap: 16px;
  align-content: start;
}
.pt-bar__top {
  display: flex;
  align-items: baseline;
  justify-content: space-between;
  gap: 8px;
  margin-bottom: 6px;
}
.pt-bar__label {
  font-size: 11px;
  font-weight: 700;
  letter-spacing: .04em;
  text-transform: uppercase;
  color: $omx-ink-500;
}
.pt-bar__pct { font-size: 14px; font-weight: 800; font-variant-numeric: tabular-nums; color: $omx-ink-900; }
.pt-bar__track {
  height: 8px;
  border-radius: 999px;
  background: $omx-ink-100;
  overflow: hidden;
}
.pt-bar__fill { display: block; height: 100%; border-radius: 999px; }
.pt-bar__fill--brand { background: $omx-brand-600; }
.pt-bar__fill--good  { background: $omx-success-600; }
.pt-bar__fill--bad   { background: $omx-danger-600; }

@media (max-width: 900px) {
  .pt-body { grid-template-columns: 1fr; }
}
</style>
